import { computed, ref, watch } from 'vue'
import { getIdempotencyKey } from '@shared/utils'
import { resolveTradingError, useCreateListingMutation, type MyListing } from '../../trading'
import {
  CREATE_LISTING_FAILED,
  DIVIDEND_NOTICE_REQUIRED,
  SELL_TERMS_REQUIRED,
} from '../constants/sellCopy'

// Confirm Listing → create: the terms tick, the create-listing call and its
// inline error (spec copy per error code), and the listing that comes back
// (Under Review or Rejected).

interface ListingRequest {
  propertyId: string
  tokens: number
  pricePerToken: number
  /** The investor acknowledged the dividend-timing notice. */
  dividendNoticeAccepted: boolean
}

export function useSellListingSubmit() {
  const mutation = useCreateListingMutation()
  const hasAcceptedTerms = ref(false)
  const error = ref<string | null>(null)
  const listing = ref<MyListing | null>(null)

  // One key per submit attempt: retrying the same listing reuses it (the
  // server returns the original listing); a changed listing is a new attempt.
  const idempotencyKey = ref(getIdempotencyKey())
  let lastRequest = ''

  watch(hasAcceptedTerms, (accepted) => {
    if (accepted && error.value === SELL_TERMS_REQUIRED) error.value = null
  })

  /** Creates the listing; false when the terms are unticked or the call fails. */
  const submit = async (request: ListingRequest): Promise<boolean> => {
    error.value = null
    if (!hasAcceptedTerms.value) {
      error.value = SELL_TERMS_REQUIRED
      return false
    }
    if (!request.dividendNoticeAccepted) {
      error.value = DIVIDEND_NOTICE_REQUIRED
      return false
    }
    const signature = `${request.propertyId}:${request.tokens}:${request.pricePerToken}`
    if (signature !== lastRequest) {
      idempotencyKey.value = getIdempotencyKey()
      lastRequest = signature
    }
    try {
      listing.value = await mutation.mutateAsync({
        propertyId: request.propertyId,
        tokens: request.tokens,
        pricePerToken: request.pricePerToken,
        dividendNoticeAccepted: true,
        termsAccepted: true,
        idempotencyKey: idempotencyKey.value,
      })
      return true
    } catch (caught) {
      error.value = resolveTradingError(caught, CREATE_LISTING_FAILED)
      return false
    }
  }

  const reset = () => {
    hasAcceptedTerms.value = false
    error.value = null
    listing.value = null
    lastRequest = ''
    idempotencyKey.value = getIdempotencyKey()
  }

  return {
    hasAcceptedTerms,
    error,
    listing,
    isSubmitting: computed(() => mutation.isPending.value),
    submit,
    reset,
  }
}
